import type {
    Coupon,
    CreateMapRequest,
    CreateOrderRequest,
    CreateOrderResponse,
    Discount,
    ListAdminOrdersResponse,
    ListCouponsResponse,
    ListMapsResponse,
    ListOrdersResponse,
    Map,
    Order,
    PayOrderResponse,
    PaymentVerification,
    RefundRequest,
    UpdateFulfillmentRequest,
} from "./types";

// Auth is carried by the HttpOnly `topograph_session` cookie set by
// /api/oidc-callback. We just need `credentials: "include"` so the browser
// attaches it on every same-origin fetch.
const BASE = "/api";

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";

    const resp = await fetch(BASE + path, {
        method,
        headers,
        credentials: "include",
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`${method} ${path}: ${resp.status} ${text}`);
    }
    if (resp.status === 204) {
        return undefined as T;
    }
    return (await resp.json()) as T;
}

// downloadUrl appends the friendly filename to a CAS object URL so the proxy
// serves it with a Content-Disposition that names the saved file after `name`
// instead of its CAS hash (the URL's last path segment). See casproxy.go.
export function downloadUrl(casUrl: string, name: string): string {
    return `${casUrl}?name=${encodeURIComponent(name)}`;
}

export const CLIENT = {
    createMap: (req: CreateMapRequest) => request<Map>("POST", "/maps", req),
    listMaps: (owner: string) =>
        request<ListMapsResponse>("GET", `/maps?owner=${encodeURIComponent(owner)}`),
    getMap: (id: string) => request<Map>("GET", `/maps/${encodeURIComponent(id)}`),
    deleteMap: (id: string) => request<Map>("DELETE", `/maps/${encodeURIComponent(id)}`),

    // Checkout / orders (the caller's own).
    checkDiscount: (code: string) =>
        request<Discount>("GET", `/discounts/${encodeURIComponent(code)}`),
    createOrder: (req: CreateOrderRequest) =>
        request<CreateOrderResponse>("POST", "/orders", req),
    listOrders: () => request<ListOrdersResponse>("GET", "/orders"),
    payOrder: (id: string) =>
        request<PayOrderResponse>("GET", `/orders/${encodeURIComponent(id)}/pay`),
    cancelOrder: (id: string) =>
        request<Order>("POST", `/orders/${encodeURIComponent(id)}/cancel`),

    // Admin surface — requires a session elevated via /api/admin/login
    // (the topograph.admin scope).
    adminListOrders: () => request<ListAdminOrdersResponse>("GET", "/admin/orders"),
    adminVerifyPayment: (id: string) =>
        request<PaymentVerification>("GET", `/admin/orders/${encodeURIComponent(id)}/payment`),
    adminRefund: (id: string, req: RefundRequest) =>
        request<PaymentVerification>("POST", `/admin/orders/${encodeURIComponent(id)}/refund`, req),
    adminSetFulfillment: (id: string, req: UpdateFulfillmentRequest) =>
        request<Order>("POST", `/admin/orders/${encodeURIComponent(id)}/fulfillment`, req),
    adminListCoupons: () => request<ListCouponsResponse>("GET", "/admin/coupons"),
    adminPutCoupon: (coupon: Coupon) =>
        request<Coupon>("PUT", `/admin/coupons/${encodeURIComponent(coupon.code)}`, coupon),
    adminDeleteCoupon: (code: string) =>
        request<void>("DELETE", `/admin/coupons/${encodeURIComponent(code)}`),
};
